import { Injectable, inject } from '@angular/core';
import { createActor, fromCallback, fromEventObservable } from 'xstate';

import { isInternalUser } from '@b2b/common/services/auth/utils/is-internal-user';
import type { Impersonation, UserSession } from '@b2b/common/services/auth/types';
import type { TODO } from '@b2b/types/core/utils';

import { AuthenticationService } from '../auth/authentication.service';
import { createAuthStateObservable } from './machines/pendo-authentication-state';
import { createPendoReadinessMonitor } from './machines/pendo-readiness-monitor';
import { pendoMachine } from './machines/pendo.machine';

@Injectable({ providedIn: 'root' })
export class PendoService {
  private readonly authenticationService = inject(AuthenticationService);
  private readonly $window: Window = window;

  private actor = this.createActor();

  /**
   * Initialize Pendo SDK
   * This should be called once when the application starts.
   * Actual SDK initialization is handled by the state machine when the Pendo agent becomes ready.
   */
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  init(): void {
    // Initialization is delegated to the state machine via the initializePendo action,
    // which fires when the Pendo agent becomes ready.
  }

  /**
   * Track a page load (call on route changes)
   * @param metadata.routeName maps to ui-router state
   */
  trackPageLoad(metadata: Partial<{ routeName: string }> = {}): void {
    this.actor.send({ type: 'TRACK_PAGE_LOAD', params: metadata });
  }

  /**
   * Track a custom event
   */
  track(eventName: string, metadata?: Record<string, TODO>): void {
    this.actor.send({ type: 'TRACK_EVENT', params: { eventName, metadata } });
  }

  destroy(): void {
    this.actor.stop();
  }

  private createActor() {
    const inspector = window.__ACME_B2B__?.devTools?.registerInspector?.(pendoMachine.id);

    const actor = createActor(
      pendoMachine.provide({
        actors: {
          authStateObservable: fromEventObservable(() => createAuthStateObservable(this.authenticationService.state$)),
          pendoReadinessMonitor: fromCallback(({ sendBack }) => createPendoReadinessMonitor(sendBack, this.$window)),
        },
        actions: {
          initializePendo: ({ self }) => {
            const authState = this.authenticationService.getSnapshot();
            const session = authState.session;
            const impersonation = authState.impersonation ?? null;

            try {
              if (session) {
                const visitorData = this.buildVisitorData(session, impersonation);
                const accountData = this.buildAccountData(session);

                this.$window.pendo?.initialize({
                  visitor: visitorData,
                  account: accountData,
                });

                self.send({ type: 'PENDO_INITIALIZED', params: { identity: { session, impersonation } } });
              } else {
                this.$window.pendo?.initialize({
                  visitor: { id: '' },
                });

                self.send({ type: 'PENDO_INITIALIZED', params: { identity: null } });
              }
            } catch (error) {
              console.error('Pendo initialize failed:', error);
              self.send({ type: 'PENDO_INITIALIZATION_FAILED' });
            }
          },
          identify: ({ self }, params: { session: UserSession | null; impersonation: Impersonation | null }) => {
            try {
              this.identify(params.session, params.impersonation);
            } catch (error) {
              console.error('Pendo identify failed:', error);
              self.send({ type: 'PENDO_IDENTIFY_FAILED' });
            }
          },
          clearSession: () => this.clearSession(),
          trackPageLoad: () => {
            try {
              this.$window.pendo?.pageLoad();
            } catch (error) {
              console.error('Pendo pageLoad failed:', error);
            }
          },
          trackEvent: (_, params: { eventName: string; metadata?: Record<string, unknown> }) => {
            try {
              this.$window.pendo?.track(params.eventName, params.metadata as TODO);
            } catch (error) {
              console.error('Pendo track failed:', error);
            }
          },
        },
      }),
      { inspect: inspector?.inspect },
    );

    return actor.start();
  }

  private clearSession(): void {
    try {
      this.$window.pendo?.clearSession();
    } catch (error) {
      console.error('Pendo clearSession failed:', error);
    }
  }

  /**
   * Update visitor identification (e.g., when impersonation changes)
   * @param session - User session data
   * @param impersonation - Impersonation data (if any)
   * @throws Error if session is null
   */
  private identify(session: UserSession | null, impersonation: Impersonation | null): void {
    if (session == null) {
      throw new Error('No user session');
    }

    const visitorData = this.buildVisitorData(session, impersonation);
    const accountData = this.buildAccountData(session);

    this.$window.pendo?.identify({
      visitor: visitorData,
      account: accountData,
    });
  }

  /**
   * Build visitor data for Pendo
   * Strategy: Track CSR as primary visitor (always the actual person using the app)
   */
  private buildVisitorData(user: UserSession, impersonation: Impersonation | null): pendo.IdentityMetadata {
    const primaryUser = impersonation != null ? impersonation.csr : user;

    const visitorData: pendo.IdentityMetadata = {
      id: String(primaryUser.email),
      acme_id: String(primaryUser.id),
      email: primaryUser.email || null,
      user_email: primaryUser.email || null,
      user_full_name: this.buildFullName(primaryUser.firstName, primaryUser.lastName) || null,
      user_active: primaryUser.active,
      user_is_internal: isInternalUser(primaryUser.email),
      user_onboarded: primaryUser.onBoarded,
      user_title: primaryUser.title || null,
      user_username: primaryUser.userName || null,
    };

    if (impersonation != null) {
      visitorData.impersonated_user_id = String(user.id);
      visitorData.impersonated_user_email = user.email || null;
      visitorData.impersonated_user_name = this.buildFullName(user.firstName, user.lastName) || null;
    }

    return visitorData;
  }

  private buildAccountData(user: UserSession): pendo.IdentityMetadata {
    return {
      id: String(user.tenantId),
      name: user.companyName,
    };
  }

  private buildFullName(firstName: string | null, lastName: string | null): string | undefined {
    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    }
    if (firstName) {
      return firstName;
    }
    if (lastName) {
      return lastName;
    }
    return undefined;
  }
}
